<template>
  <div v-if="showModule" class="vacations-detail-module">
    <div class="vacations-detail-content" @wheel.stop>
      <div v-if="loading" class="vacations-detail-state">
        <div class="vacations-detail-loading" aria-hidden="true">
          <div class="vacations-balance-card vacations-balance-card-skeleton">
            <Skeleton width="8rem" height="0.95rem" borderRadius="999px" />
            <Skeleton width="3rem" height="1.35rem" borderRadius="999px" />
          </div>

          <div class="vacations-records-surface">
            <div class="vacations-records-header">
              <Skeleton width="4.5rem" height="0.8rem" borderRadius="999px" />
              <Skeleton width="5.5rem" height="0.8rem" borderRadius="999px" />
              <Skeleton width="2.25rem" height="0.8rem" borderRadius="999px" class="vacations-records-header-right" />
            </div>

            <div v-for="index in 4" :key="index" class="vacations-record vacations-record-skeleton">
              <div class="vacations-record-period">
                <Skeleton width="8rem" height="0.95rem" borderRadius="999px" />
                <Skeleton width="4.25rem" height="0.8rem" borderRadius="999px" />
              </div>
              <div class="vacations-record-copy">
                <Skeleton width="7rem" height="0.8rem" borderRadius="999px" />
                <Skeleton width="11rem" height="0.95rem" borderRadius="999px" />
              </div>
              <div class="vacations-record-values">
                <Skeleton width="2.5rem" height="0.95rem" borderRadius="999px" />
                <Skeleton width="2rem" height="0.8rem" borderRadius="999px" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="error" class="vacations-detail-state">
        <EmptyState
          :title="t('wkt.client.vacations.error-state.title')"
          :description="t('wkt.client.vacations.error-state.description')"
        />
      </div>

      <div v-else-if="records.length === 0" class="vacations-detail-state">
        <EmptyState
          :title="t('wkt.client.vacations.detail.empty-state.title')"
          :description="t('wkt.client.vacations.detail.empty-state.description')"
        />
      </div>

      <template v-else>
        <div v-if="showAvailableDays" class="vacations-balance-card">
          <span class="vacations-balance-label">{{ t('wkt.client.vacations-available-days') }}</span>
          <span class="vacations-balance-value">{{ formatDays(availableDays) }}</span>
        </div>

        <div class="vacations-records-surface">
          <div class="vacations-records-header">
            <span>{{ t('wkt.client.vacations-period') }}</span>
            <span>{{ t('wkt.client.vacations-description') }}</span>
            <span class="vacations-records-header-right">{{ t('wkt.client.vacations-days') }}</span>
          </div>

          <button
            v-for="record in records"
            :key="record.id ?? `${record.recordDateTs}-${record.recordTitle}`"
            type="button"
            class="vacations-record"
            @click="selectedRecord = record"
          >
            <div class="vacations-record-period">
              <span class="vacations-record-main">{{ formatPeriod(record) }}</span>
            </div>

            <div class="vacations-record-copy">
              <span class="vacations-record-title">{{ record.recordTitle || "-" }}</span>
              <span class="vacations-record-type">{{ getRecordTypeLabel(record.recordType) }}</span>
              <span v-if="showRemainingDays(record)" class="vacations-record-remaining">
                {{ formatRemainingDaysMessage(record) }}
              </span>
            </div>

            <div class="vacations-record-values">
              <span :class="['vacations-delta', getDeltaClass(record.hoursBagDelta)]">
                {{ formatSignedDays(record.hoursBagDelta) }}
              </span>
              <span v-if="record.hoursBagType !== null && record.hoursBagType !== undefined" class="vacations-total">
                {{ formatDays(record.hoursBagTotal) }}
              </span>
            </div>
          </button>
        </div>
      </template>
    </div>

    <Dialog
      v-model:visible="detailVisible"
      modal
      :header="selectedRecord ? getDialogTitle(selectedRecord) : ''"
      class="vacations-detail-dialog"
      :style="{ width: 'min(560px, calc(100vw - 24px))' }"
    >
      <div v-if="selectedRecord" class="vacations-dialog-grid">
        <div class="vacations-dialog-cell">
          <span class="vacations-dialog-label">{{ t('wkt.client.vacations-date') }}</span>
          <span class="vacations-dialog-value">{{ formatRecordDate(selectedRecord.recordDateTs) }}</span>
        </div>

        <div v-if="formatPeriod(selectedRecord)" class="vacations-dialog-cell">
          <span class="vacations-dialog-label">{{ t('wkt.client.vacations-period') }}</span>
          <span class="vacations-dialog-value">{{ formatPeriod(selectedRecord) }}</span>
        </div>

        <div v-if="showRemainingDays(selectedRecord)" class="vacations-dialog-cell">
          <span class="vacations-dialog-label">{{ t('wkt.client.vacations-remaining-days') }}</span>
          <span class="vacations-dialog-value">{{ formatDays(selectedRecord.remainingDays) }}</span>
        </div>

        <div v-if="showRemainingDays(selectedRecord)" class="vacations-dialog-cell">
          <span class="vacations-dialog-label">{{ t('wkt.client.vacations-expiration-date') }}</span>
          <span class="vacations-dialog-value">{{ formatSimpleDate(selectedRecord.expirationDate) }}</span>
        </div>

        <div class="vacations-dialog-cell">
          <span class="vacations-dialog-label">{{ getDaysLabel(selectedRecord) }}</span>
          <span :class="['vacations-dialog-value', getDeltaClass(selectedRecord.hoursBagDelta)]">
            {{ formatSignedDays(selectedRecord.hoursBagDelta) }}
          </span>
        </div>

        <div v-if="shouldShowTotalValue(selectedRecord)" class="vacations-dialog-cell">
          <span class="vacations-dialog-label">{{ getTotalLabel(selectedRecord) }}</span>
          <span class="vacations-dialog-value">{{ formatDays(getTotalValue(selectedRecord)) }}</span>
        </div>

        <div class="vacations-dialog-cell vacations-dialog-cell-wide">
          <span class="vacations-dialog-label">{{ t('wkt.client.vacations-description') }}</span>
          <span class="vacations-dialog-value">{{ selectedRecord.recordTitle || "-" }}</span>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import type {
  VacationRecordDTO,
  VacationsDetailModulePayload,
} from "@wkt/api/vacationsDTO";
import EmptyState from "@wkt/components/common/EmptyState.vue";
import { useDateFormatter } from "@wkt/composables/useDateFormatter";

const RECORD_TYPE_PERIOD_START_BALANCE = 0;
const RECORD_TYPE_REGULARIZATION = 1;
const RECORD_TYPE_WORK_ACCRUED = 2;
const RECORD_TYPE_COMPLETE_DAY = 7;
const RECORD_TYPE_FOR_HOURS = 8;

const { t, locale } = useI18n();
const { formatOnlyDate, formatSimpleDate } = useDateFormatter();

const showModule = ref(false);
const loading = ref(true);
const error = ref(false);
const title = ref<string>("---");
const availableDays = ref<number>(0);
const showAvailableDays = ref(false);
const advancedVacationEnabled = ref(false);
const records = ref<VacationRecordDTO[]>([]);
const selectedRecord = ref<VacationRecordDTO | null>(null);

const detailVisible = computed({
  get: () => selectedRecord.value !== null,
  set: (value: boolean) => {
    if (!value) {
      selectedRecord.value = null;
    }
  },
});

function isVacationRecord(item: unknown): item is VacationRecordDTO {
  return Boolean(item) && typeof item === "object";
}

function isVacationsDetailPayload(payload: unknown): payload is VacationsDetailModulePayload {
  return Boolean(payload) && typeof payload === "object";
}

function formatDays(value?: number | null): string {
  return new Intl.NumberFormat([locale.value, "en"], {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value ?? 0);
}

function formatSignedDays(value?: number | null): string {
  const normalized = value ?? 0;
  if (normalized > 0) {
    return `+${formatDays(normalized)}`;
  }
  return formatDays(normalized);
}

function formatRecordDate(value?: number | null): string {
  return value ? formatOnlyDate(value) : "";
}

function formatPeriod(record: VacationRecordDTO): string {
  const start = formatSimpleDate(record.fromDate);
  const end = formatSimpleDate(record.toDate);

  if (start && end) {
    return `${start} - ${end}`;
  }
  return start || end || "";
}

function getRecordTypeLabel(type?: number | null): string {
  switch (type) {
    case RECORD_TYPE_PERIOD_START_BALANCE:
      return t("wkt.client.vacations.types.opening_period");
    case RECORD_TYPE_REGULARIZATION:
      return t("wkt.client.vacations.types.regularization");
    case RECORD_TYPE_WORK_ACCRUED:
      return t("wkt.client.vacations.types.work_accrued");
    case RECORD_TYPE_COMPLETE_DAY:
      return t("wkt.client.vacations.types.complete_day");
    case RECORD_TYPE_FOR_HOURS:
      return t("wkt.client.vacations.types.for_hours");
    default:
      return "---";
  }
}

function getDeltaClass(value?: number | null): string {
  if ((value ?? 0) < 0) {
    return "vacations-delta-negative";
  }
  if ((value ?? 0) > 0) {
    return "vacations-delta-positive";
  }
  return "vacations-delta-neutral";
}

function showRemainingDays(record: VacationRecordDTO): boolean {
  return Boolean(
    advancedVacationEnabled.value &&
      record.expirationDate &&
      (record.hoursBagDelta ?? 0) > 0
  );
}

function formatRemainingDaysMessage(record: VacationRecordDTO): string {
  const template = t("wkt.client.vacations-remaining-days-expire");
  return template
    .replace("%s", formatDays(record.remainingDays))
    .replace("%s", formatSimpleDate(record.expirationDate));
}

function getDialogTitle(record: VacationRecordDTO): string {
  return `${title.value} - ${getRecordTypeLabel(record.recordType)}`;
}

function getDaysLabel(record: VacationRecordDTO): string {
  if (record.recordType === RECORD_TYPE_PERIOD_START_BALANCE) {
    return `${title.value} (${record.exercise ?? "-"})`;
  }
  return t("wkt.client.vacations-diference-days");
}

function getTotalLabel(record: VacationRecordDTO): string {
  if (record.recordType === RECORD_TYPE_PERIOD_START_BALANCE) {
    return `${title.value} (${t("wkt.client.vacations-days.new-record-window.pending")} ${(record.exercise ?? 1) - 1})`;
  }
  return t("wkt.client.vacations-total-days");
}

function shouldShowTotalValue(record: VacationRecordDTO): boolean {
  return (
    record.recordType === RECORD_TYPE_PERIOD_START_BALANCE ||
    record.hoursBagType !== null && record.hoursBagType !== undefined
  );
}

function getTotalValue(record: VacationRecordDTO): number {
  if (record.recordType === RECORD_TYPE_PERIOD_START_BALANCE) {
    return (record.hoursBagTotal ?? 0) - (record.hoursBagDelta ?? 0);
  }
  return record.hoursBagTotal ?? 0;
}

function applyPayload(payload?: unknown): void {
  if (!isVacationsDetailPayload(payload)) {
    loading.value = true;
    error.value = false;
    records.value = [];
    selectedRecord.value = null;
    return;
  }

  loading.value = Boolean(payload.loading);
  error.value = Boolean(payload.error);
  title.value = payload.title?.trim() || "---";
  availableDays.value = typeof payload.availableDays === "number" ? payload.availableDays : 0;
  showAvailableDays.value = Boolean(payload.showAvailableDays);
  advancedVacationEnabled.value = Boolean(payload.advancedVacationEnabled);
  records.value = Array.isArray(payload.records)
    ? payload.records.filter(isVacationRecord)
    : [];
  selectedRecord.value = null;
}

function onReveal(payload?: unknown): void {
  showModule.value = true;
  applyPayload(payload);
}

function refreshModule(): void {
  showModule.value = true;
}

defineExpose({
  onReveal,
  refreshModule,
});
</script>

<style scoped>
.vacations-detail-module {
  min-height: 100%;
}

.vacations-detail-content {
  min-height: 100%;
  padding: 18px 12px 24px;
}

.vacations-detail-state {
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.vacations-detail-loading {
  width: 100%;
}

.vacations-balance-card {
  margin-bottom: 14px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.vacations-balance-card-skeleton {
  background: #ffffff;
}

.vacations-balance-label {
  color: #334155;
  font-size: 0.9rem;
  font-weight: 700;
}

.vacations-balance-value {
  color: #16a34a;
  font-size: 1.2rem;
  font-weight: 700;
}

.vacations-records-surface {
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 14px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.vacations-records-header {
  display: grid;
  grid-template-columns: 1.15fr 1.8fr 0.55fr;
  gap: 12px;
  padding: 12px 14px;
  background: rgba(148, 163, 184, 0.08);
  color: #475569;
  font-size: 0.72rem;
  font-weight: 700;
}

.vacations-records-header-right {
  text-align: right;
}

.vacations-record {
  width: 100%;
  border: 0;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
  background: transparent;
  display: grid;
  grid-template-columns: 1.15fr 1.8fr 0.55fr;
  gap: 12px;
  padding: 12px 14px;
  text-align: left;
  cursor: pointer;
}

.vacations-record:hover {
  background: rgba(var(--primary-rgb, 24, 94, 169), 0.04);
}

.vacations-record-skeleton {
  cursor: default;
}

.vacations-record-skeleton:hover {
  background: transparent;
}

.vacations-record-period,
.vacations-record-copy,
.vacations-record-values {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.vacations-record-values {
  align-items: flex-end;
}

.vacations-record-main,
.vacations-record-title {
  color: #0f172a;
  font-size: 0.84rem;
  font-weight: 500;
  line-height: 1.35;
}

.vacations-record-secondary,
.vacations-record-type,
.vacations-total,
.vacations-record-remaining {
  color: #64748b;
  font-size: 0.72rem;
  line-height: 1.35;
}

.vacations-record-remaining {
  color: #475569;
}

.vacations-delta {
  font-size: 0.86rem;
  font-weight: 700;
}

.vacations-delta-positive {
  color: #16a34a;
}

.vacations-delta-negative {
  color: #dc2626;
}

.vacations-delta-neutral {
  color: #475569;
}

.vacations-dialog-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.vacations-dialog-cell {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.vacations-dialog-cell-wide {
  grid-column: 1 / -1;
}

.vacations-dialog-label {
  color: #0f172a;
  font-size: 0.84rem;
  font-weight: 700;
}

.vacations-dialog-value {
  color: #475569;
  font-size: 0.92rem;
  line-height: 1.45;
}

@media (max-width: 767px) {
  .vacations-detail-content {
    padding: 10px 8px 16px;
  }

  .vacations-balance-card {
    margin-bottom: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    gap: 8px;
  }

  .vacations-balance-label {
    font-size: 0.8rem;
  }

  .vacations-balance-value {
    font-size: 1rem;
  }

  .vacations-records-surface {
    border-radius: 10px;
    box-shadow: none;
  }

  .vacations-records-header {
    grid-template-columns: minmax(6.5rem, 1.05fr) minmax(0, 1.55fr) minmax(2.25rem, 0.45fr);
    gap: 8px;
    padding: 10px 12px;
    font-size: 0.66rem;
  }

  .vacations-record {
    grid-template-columns: minmax(6.5rem, 1.05fr) minmax(0, 1.55fr) minmax(2.25rem, 0.45fr);
    gap: 8px;
    padding: 10px 12px;
    align-items: start;
  }

  .vacations-record-period {
    gap: 2px;
  }

  .vacations-record-copy {
    gap: 2px;
  }

  .vacations-record-values {
    align-items: flex-end;
    justify-content: flex-start;
    gap: 2px;
    min-width: 0;
  }

  .vacations-record-main,
  .vacations-record-title {
    font-size: 0.78rem;
    line-height: 1.25;
  }

  .vacations-record-secondary,
  .vacations-record-type,
  .vacations-total,
  .vacations-record-remaining {
    font-size: 0.68rem;
    line-height: 1.25;
  }

  .vacations-record-title {
    font-weight: 700;
  }

  .vacations-delta {
    font-size: 0.8rem;
  }

  .vacations-dialog-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .vacations-dialog-cell {
    gap: 4px;
  }

  .vacations-dialog-label {
    font-size: 0.78rem;
  }

  .vacations-dialog-value {
    font-size: 0.84rem;
    line-height: 1.35;
  }
}
</style>
